import { useState, useRef, useEffect } from "react"
import { useQuery } from "react-query"
import { useTranslation } from "react-i18next"
import { searchProducts } from "../../../../services/containerTemplateServices"

const getProductSku = (product) => {
    if (!product) return null
    if (product.product_codes?.length > 0) return product.product_codes[0].sku
    return product.sku || null
}

const getProductBarcode = (product) => {
    if (!product) return null
    return product.bar_code || product.barcode || null
}

export const ProductSearchInput = ({ storeId, value, onChange }) => {
    const { i18n } = useTranslation()
    const [searchTerm, setSearchTerm] = useState('')
    const [isOpen, setIsOpen] = useState(false)
    const [debouncedTerm, setDebouncedTerm] = useState('')
    const wrapperRef = useRef(null)
    const debounceTimer = useRef(null)

    // Debounce search term
    useEffect(() => {
        if (debounceTimer.current) clearTimeout(debounceTimer.current)
        debounceTimer.current = setTimeout(() => {
            setDebouncedTerm(searchTerm)
        }, 300)
        return () => clearTimeout(debounceTimer.current)
    }, [searchTerm])

    // Search products
    const { data: productsData, isLoading } = useQuery(
        ['product-search', storeId, debouncedTerm],
        () => searchProducts({ store_id: storeId, search: debouncedTerm, per_page: 10 }),
        {
            enabled: !!storeId && debouncedTerm.length >= 2,
            refetchOnWindowFocus: false,
        }
    )

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const products = productsData?.products || productsData || []

    const handleSelect = (product) => {
        onChange(product)
        setSearchTerm(product.name)
        setIsOpen(false)
    }

    const displayValue = value?.name ? value.name : searchTerm

    return (
        <div ref={wrapperRef} className="relative">
            <input
                type="text"
                className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={i18n.t('container_templates.editor.product_search_placeholder')}
                value={displayValue}
                onChange={(e) => {
                    setSearchTerm(e.target.value)
                    if (value) onChange(null)
                    setIsOpen(true)
                }}
                onFocus={() => { if (searchTerm.length >= 2 || products.length > 0) setIsOpen(true) }}
            />
            {value?.name && (
                <div className="mt-1 text-xs text-gray-500">
                    {value.name}
                    {getProductSku(value) ? ` | SKU: ${getProductSku(value)}` : ''}
                    {getProductBarcode(value) ? ` | ${getProductBarcode(value)}` : ''}
                </div>
            )}
            {isOpen && (
                <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {isLoading ? (
                        <div className="p-3 text-sm text-gray-500 text-center">...</div>
                    ) : products.length === 0 ? (
                        <div className="p-3 text-sm text-gray-500 text-center">
                            {debouncedTerm.length >= 2 ? i18n.t('container_templates.editor.no_products_found') : ''}
                        </div>
                    ) : (
                        (Array.isArray(products) ? products : []).map((product) => (
                            <div
                                key={product.id}
                                className="px-3 py-2 text-sm hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                                onClick={() => handleSelect(product)}
                            >
                                <div className="font-medium">{product.name}</div>
                                <div className="text-xs text-gray-500">
                                    SKU: {getProductSku(product) || '-'} {getProductBarcode(product) ? `| ${getProductBarcode(product)}` : ''}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    )
}
